/**
 * Format number as Indian currency (INR)
 */
export function formatCurrency(amount, currency = 'INR') {
  if (amount === null || amount === undefined) return '₹0.00';

  // Check if the amount has decimal places
  const hasDecimals = amount % 1 !== 0;

  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });

  return formatter.format(amount);
}

/**
 * Format number with Indian number system (lakhs, crores)
 */
export function formatIndianNumber(num) {
  if (num === null || num === undefined) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 10000000) {
    return sign + (absNum / 10000000).toFixed(2) + ' Cr';
  } else if (absNum >= 100000) {
    return sign + (absNum / 100000).toFixed(2) + ' L';
  } else if (absNum >= 1000) {
    return sign + (absNum / 1000).toFixed(1) + ' K';
  }

  return sign + absNum.toString();
}

/**
 * Format date for display (DD-MM-YYYY)
 */
export function formatDate(date, format = 'display') {
  if (!date) return '';

  const d = new Date(date);

  // Check for invalid date
  if (isNaN(d.getTime())) return '';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (format === 'display' || format === 'short') {
    // DD-MM-YYYY format for forms/add/edit
    return `${day}-${month}-${year}`;
  }

  if (format === 'list') {
    // DD Mon YYYY format for listings (e.g., 09 Apr 2025)
    return `${day} ${monthShort[d.getMonth()]} ${year}`;
  }

  if (format === 'long') {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${day} ${monthNames[d.getMonth()]} ${year}`;
  }

  if (format === 'input') {
    // HTML date input requires YYYY-MM-DD
    return `${year}-${month}-${day}`;
  }

  return `${day}-${month}-${year}`;
}

/**
 * Parse DD-MM-YYYY string to Date object
 */
export function parseDate(dateString) {
  if (!dateString) return null;

  // If already in YYYY-MM-DD format (from input)
  if (dateString.includes('-') && dateString.indexOf('-') === 4) {
    return new Date(dateString);
  }

  // Parse DD-MM-YYYY
  const [day, month, year] = dateString.split('-');
  return new Date(year, month - 1, day);
}

/**
 * Format percentage
 */
export function formatPercentage(value, decimals = 1) {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Capitalize first letter of each word
 */
export function capitalizeWords(str) {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get category display name
 */
export function getCategoryLabel(category) {
  const labels = {
    // Income categories
    salary: 'Salary',
    freelance: 'Freelance',
    business: 'Business',
    interest: 'Interest',
    dividend: 'Dividend',
    other_income: 'Other Income',
    // Expense categories
    personal_loan: 'Personal Loan',
    home_loan: 'Home Loan',
    sip: 'SIP Investment',
    rent: 'Rent',
    utilities: 'Utilities',
    food: 'Food & Dining',
    transport: 'Transport',
    healthcare: 'Healthcare',
    insurance: 'Insurance',
    tax_payment: 'Tax Payment',
    other_expense: 'Other Expense'
  };

  return labels[category] || capitalizeWords(category);
}

/**
 * Get financial quarter from date
 */
export function getQuarter(date) {
  const d = new Date(date);
  const month = d.getMonth();

  if (month >= 3 && month <= 5) return 'Q1';
  if (month >= 6 && month <= 8) return 'Q2';
  if (month >= 9 && month <= 11) return 'Q3';
  return 'Q4';
}

/**
 * Get financial year from date
 */
export function getFinancialYear(date) {
  const d = new Date(date);
  const month = d.getMonth();
  const year = d.getFullYear();

  if (month >= 3) {
    return `${year}-${(year + 1).toString().slice(-2)}`;
  }
  return `${year - 1}-${year.toString().slice(-2)}`;
}

/**
 * Get current time in HH:mm format (24-hour)
 */
export function getCurrentTime() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}
